import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLanguage } from "@/i18n/LanguageProvider";
import { createViewUrl } from "@/lib/downloadFile";

/**
 * DeliverableImageViewer — modal ÚNICO de visualização de imagem gerada
 * (PR-A F2): extraído do Dialog inline da Biblioteca pra ser compartilhado
 * entre Library e MirrorChat (imagem inline na bolha). Mesma mecânica de lá:
 * <img> com a signed URL de view (streaming do browser, sem blob na heap;
 * fonte createViewUrl). A resolução da URL vive AQUI — os callers só passam
 * {title, storagePath} e o modal abre na hora com skeleton enquanto assina.
 */

export interface ImageViewTarget {
  title: string;
  storagePath: string;
}

interface DeliverableImageViewerProps {
  target: ImageViewTarget | null;
  onClose: () => void;
}

const DeliverableImageViewer = ({ target, onClose }: DeliverableImageViewerProps) => {
  const { t } = useLanguage();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!target) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    createViewUrl(target.storagePath)
      .then((u) => {
        if (!cancelled) setUrl(u);
      })
      .catch((err) => {
        console.error("[DeliverableImageViewer] sign failed:", err);
        if (!cancelled) {
          toast.error(t("library.viewError"));
          onClose();
        }
      });
    return () => {
      cancelled = true;
    };
    // onClose intencionalmente fora das deps: é identidade de callback do
    // caller; re-assinar por re-render do pai seria trabalho à toa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, t]);

  return (
    <Dialog open={target !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl w-[95vw] max-h-[88vh] flex flex-col p-3">
        <DialogHeader>
          <DialogTitle className="text-left text-[15px] truncate pr-6">
            {target?.title}
          </DialogTitle>
        </DialogHeader>
        <div
          className="flex-1 min-h-0 rounded-xl overflow-hidden flex items-center justify-center"
          style={{ background: "hsl(222 18% 10%)", border: "1px solid hsl(222 18% 18%)" }}
        >
          {target && url ? (
            <img
              src={url}
              alt={target.title}
              className="max-w-full max-h-[70vh] object-contain"
              onError={() => {
                toast.error(t("library.viewError"));
                onClose();
              }}
            />
          ) : (
            // Skeleton enquanto a signed URL é emitida (round-trip único).
            <div
              className="w-full h-[40vh] animate-pulse"
              style={{ background: "hsl(222 18% 14%)" }}
            />
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="mt-1 rounded-xl font-display font-semibold text-[13px] tracking-wide transition-all active:scale-[0.98]"
          style={{ minHeight: 44, background: "hsl(187 72% 50%)", color: "hsl(222 38% 6%)" }}
        >
          {t("common.close")}
        </button>
      </DialogContent>
    </Dialog>
  );
};

export default DeliverableImageViewer;
